import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const HINTS = {
  unsupported: "This browser doesn't support push notifications.",
  no_worker: "Push works once the app is installed / opened from the home screen (production build).",
  denied: "Notifications are blocked for this site — allow them in your browser or phone settings, then try again.",
  error: "Something went wrong enabling notifications.",
};

export function PushSettingsCard() {
  const { status, error, enable, disable, isOn } = usePushNotifications();
  const [testing, setTesting] = useState(false);
  const busy = status === "working" || status === "checking";

  const sendTest = async () => {
    setTesting(true);
    try { const { data } = await api.post("/push/test"); toast.success(data.sent ? "Test notification sent — check your device" : "No devices subscribed yet"); }
    catch { toast.error("Couldn't send test"); }
    finally { setTesting(false); }
  };

  return (
    <div className="card-tactical rounded-md p-6" data-testid="push-settings-card">
      <div className="flex items-start gap-3">
        {isOn ? <BellRing className="w-5 h-5 text-[#83B68B] mt-0.5" /> : <Bell className="w-5 h-5 opacity-60 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-lg tracking-tight">Phone notifications</h2>
          <p className="text-sm opacity-70 mt-1">Get a push alert on this device when a booking you're involved in changes, an FA fixture moves, or an incident report you're part of is updated.</p>
          <div className="text-[10px] tracking-widest uppercase opacity-60 mt-3" data-testid="push-status">Status: {status === "on" ? "On for this device" : status === "off" ? "Off" : status.replace("_", " ")}</div>
          {(HINTS[status] || error) && <div className="text-xs text-amber-300 mt-1" data-testid="push-hint">{error || HINTS[status]}</div>}
          <div className="flex flex-wrap gap-2 mt-4">
            {isOn ? (
              <>
                <button type="button" onClick={sendTest} disabled={testing} data-testid="push-test-btn" className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">{testing ? "Sending…" : "Send test"}</button>
                <button type="button" onClick={disable} disabled={busy} data-testid="push-disable-btn" className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5 opacity-80 hover:opacity-100"><BellOff className="w-3 h-3" /> Turn off</button>
              </>
            ) : (
              <button type="button" onClick={enable} disabled={busy || status === "unsupported" || status === "no_worker"} data-testid="push-enable-btn" className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">{busy ? "Working…" : "Enable notifications"}</button>
            )}
          </div>
          <div className="text-[11px] opacity-50 mt-3">iPhone/iPad: add the app to your Home Screen first (Share → Add to Home Screen), open it from there, then enable.</div>
          <ChatFrequency />
        </div>
      </div>
    </div>
  );
}

const FREQ = [
  { v: "instant", label: "Instant", hint: "Every message" },
  { v: "hourly", label: "Hourly", hint: "One summary per hour" },
  { v: "daily", label: "Daily", hint: "One summary at 18:00" },
  { v: "off", label: "Off", hint: "Badge only" },
];

function ChatFrequency() {
  const [val, setVal] = useState(null);
  const [qh, setQh] = useState({ enabled: false, from: "22:00", until: "07:00" });
  useEffect(() => { api.get("/push/preferences").then(r => { setVal(r.data.chat_notify); if (r.data.quiet_hours) setQh(r.data.quiet_hours); }).catch(() => setVal("instant")); }, []);
  const choose = async (v) => {
    setVal(v);
    try { await api.put("/push/preferences", { chat_notify: v }); toast.success("Chat notifications: " + FREQ.find(f => f.v === v).label); }
    catch { toast.error("Couldn't save preference"); }
  };
  const saveQh = async (next) => {
    setQh(next);
    try { await api.put("/push/preferences", { quiet_hours: next }); toast.success(next.enabled ? `Quiet hours ${next.from}–${next.until}` : "Quiet hours off"); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't save quiet hours"); }
  };
  return (
    <div className="mt-5 pt-4 border-t border-border" data-testid="chat-notify-prefs">
      <div className="text-[10px] tracking-widest uppercase opacity-70">Chat notifications</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
        {FREQ.map(f => (
          <button key={f.v} type="button" onClick={() => choose(f.v)} disabled={val === null} data-testid={`chat-notify-${f.v}`}
            className={`rounded border px-3 py-2 text-left transition-colors ${val === f.v ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border hover:border-[#83B68B]/50"}`}>
            <div className="text-xs font-bold">{f.label}</div>
            <div className="text-[10px] opacity-60">{f.hint}</div>
          </button>
        ))}
      </div>
      <div className="text-[11px] opacity-50 mt-2">Applies to DMs, groups and @mentions. Club announcements are always sent instantly.</div>

      <div className="mt-5 pt-4 border-t border-border" data-testid="quiet-hours">
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <div className="text-[10px] tracking-widest uppercase opacity-70">Quiet hours</div>
            <div className="text-[11px] opacity-50 mt-0.5">Hold every push overnight; you'll get one "While you were away" summary when quiet hours end.</div>
          </div>
          <input type="checkbox" checked={!!qh.enabled} onChange={e => saveQh({ ...qh, enabled: e.target.checked })} data-testid="quiet-hours-toggle" className="w-4 h-4 accent-[#83B68B]" />
        </label>
        {qh.enabled && (
          <div className="flex items-center gap-3 mt-3 text-xs" data-testid="quiet-hours-times">
            <span className="opacity-70">From</span>
            <input type="time" value={qh.from} onChange={e => setQh({ ...qh, from: e.target.value })} onBlur={() => saveQh(qh)} data-testid="quiet-hours-from" className="h-9 rounded border border-border bg-background px-2" />
            <span className="opacity-70">until</span>
            <input type="time" value={qh.until} onChange={e => setQh({ ...qh, until: e.target.value })} onBlur={() => saveQh(qh)} data-testid="quiet-hours-until" className="h-9 rounded border border-border bg-background px-2" />
            <span className="opacity-50">UK time</span>
          </div>
        )}
      </div>
    </div>
  );
}
